import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import type { CommunityReactionType, IReactionSummary } from '../../../interfaces/ICommunityHub';
import type { PodService } from '../../../services/podService';
import type { PodHubService } from '../../../services/podHubService';
import { PostReactionPicker } from '../../reactions/PostReactionPicker';
import { AnimatedReactionIcon } from '../../reactions';
import { REACTION_TYPES } from '../../../utils/reactionAssets';
import { reactionTokens } from '../../../theme/tokens/reactions';
import { toast } from '../../snackbar';

interface Props {
  podId: string;
  messageId: string;
  currentUserId: string;
  canReact: boolean;
  service: PodService;
  hub: PodHubService;
}

export default function PodMessageReactions({ podId, messageId, currentUserId, canReact, service, hub }: Props) {
  const [response, setResponse] = useState<{ key: string; summary: IReactionSummary | null; error: boolean } | null>(null);
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const key = `${podId}:${messageId}:${currentUserId}:${revision}`;
  const ready = response?.key === key;
  const summary = response?.summary;
  useEffect(() => {
    let active = true;
    void service.getMessageReactions(podId, messageId)
      .then(data => { if (active) setResponse({ key, summary: data, error: false }); })
      .catch(() => { if (active) setResponse({ key, summary: null, error: true }); });
    return () => { active = false; };
  }, [service, podId, messageId, key]);
  useEffect(() => {
    const changed = hub.on('message.reaction', payload => {
      if (payload.podId === podId && payload.messageId === messageId) setRevision(value => value + 1);
    });
    const reconnected = hub.onReconnected(() => setRevision(value => value + 1));
    return () => { changed(); reconnected(); };
  }, [hub, podId, messageId]);

  const select = async (reaction: CommunityReactionType) => {
    if (!ready || !summary || pending.current) return;
    pending.current = true;
    setSaving(true);
    try {
      if (summary.userReaction === reaction) await service.removeMessageReaction(podId, messageId);
      else await service.setMessageReaction(podId, messageId, reaction);
      setRevision(value => value + 1);
    } catch {
      toast.error("We couldn't update your reaction. Please try again.");
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
    {REACTION_TYPES.filter(type => (summary?.counts[type] ?? 0) > 0).map(type => <Box key={type}
      aria-label={`${type}: ${summary!.counts[type].toLocaleString('en-GB')}`}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: summary?.userReaction === type ? 'primary.main' : 'text.secondary' }}>
      <AnimatedReactionIcon reaction={type} size={reactionTokens.summary} />
      <Typography variant="caption">{summary!.counts[type].toLocaleString('en-GB')}</Typography>
    </Box>)}
    {ready && response.error ? <IconButton title="Retry loading reactions" aria-label="Retry loading reactions"
      onClick={() => setRevision(value => value + 1)} sx={{ minWidth: reactionTokens.slot, minHeight: reactionTokens.slot }}><Refresh /></IconButton>
      : canReact && <PostReactionPicker message userReaction={summary?.userReaction ?? null}
        disabled={!ready || saving} onSelect={reaction => { void select(reaction); }} />}
  </Box>;
}